<template>
  <span>
    <modal @close="$emit('close')">
      <h4 slot="title">{{ popupTitle }}</h4>
      <section slot="body" class="popup-section-body">
        <div v-if="confirmationStage">
          <div v-if="team && team.name" :class="{ 'animation-shake': errorShake }" :style="{ marginBottom: '18px' }">
            <p>
              {{ $t('pleaseType') }}<span class="name-to-type"> {{ team.name }} </span>{{ $t('toConfirm') }}
            </p>
            <editable-input id="input-team-name" v-model="teamName" nameToSave="organization" :validation-function="teamNameValidation" />
          </div>
          <p>{{ $t('password') }}</p>
          <editable-input v-model="password" nameToSave="password" input-type="password" class="input-std" />
        </div>
        <div v-else>
          <p v-if="!formValid && !initial" class="confirmation-reminder">
            {{ $t('confirmUnderstandRamifications') }}
          </p>
          <p v-if="cancelSubscriptionProp" class="warning">
            {{ $t('beforeYouDowngrade') }} <span class="headsup">{{ $t('justAHeadUp') }}</span>
          </p>
          <p v-else class="warning">{{ text.warning }}</p>
          <checkbox
            v-for="(el, index) in checkboxesArgs"
            :key="'checkbox' + index"
            v-model="el.checked"
            class="check"
            :remind="!formValid && !initial"
            :content="el.term"
            color="red"
            @input="initial = false"
          />
        </div>
        <div class="buttons">
          <button id="delete-button" class="clavo-button clavo-button-danger-primary" @click="deleteAccount">
            {{ confirmationStage ? $t('confirm') : buttonDeleteText }}
          </button>
          <button id="cancel-button" class="clavo-button secondary" @click="$emit('close')">
            {{ confirmationStage ? $t('cancel') : buttonCancelText }}
          </button>
        </div>
      </section>
    </modal>
  </span>
</template>

<script>
import Modal from '@/components/Modal'
import Constants from '@/components/Constants'
import EditableInput from '@/components/UI/EditableInput'
import Checkbox from '@/components/UI/Checkbox'
import ParseFunctions from '@/components/ParseFunctions'

export default {
  components: {
    Modal,
    Checkbox,
    EditableInput,
  },
  mixins: [Constants, ParseFunctions],
  props: {
    activeMembersCount: {
      type: Number,
      default: 0,
    },
    team: {
      type: Object,
      default: undefined,
    },
    cancelSubscriptionProp: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      text: this.team ? this.texts().popup.deleteAccount.team : this.texts().popup.deleteAccount.user,
      email: '',
      initial: true, // don't remind user to tick terms immediately after he/she opens modal
      dummy: false,
      textConfirmation: '',
      buttonDeleteText: '',
      buttonCancelText: '',
      popupTitle: '',
      checkboxesArgs: [],
      teamName: '',
      password: '',
      errorShake: false,
      confirmationStage: false,
    }
  },
  computed: {
    formValid() {
      let args = true
      for (let i = 0; i < this.checkboxesArgs.length; i++) {
        if (!this.checkboxesArgs[i].checked) args = false
      }
      return args
    },
  },
  async created() {
    if (this.team) {
      this.text = this.cancelSubscriptionProp ? this.texts().popup.deleteAccount.cancelSubscription : this.texts().popup.deleteAccount.team
    } else {
      this.text = this.texts().popup.deleteAccount.user
    }
    if (!this.cancelSubscriptionProp) {
      this.popupTitle = this.team ? this.$t('deleteTeam') : this.$t('deleteAccount')
      this.textConfirmation = this.text.confirmation
      this.buttonDeleteText = this.$t('delete')
      this.buttonCancelText = this.$t('cancel')
    } else {
      this.popupTitle = this.$t('cancelSubscription')
      this.textConfirmation = this.$tc('currentlyHaveActive', this.activeMembersCount > 1 ? 0 : 1, { count: this.activeMembersCount })
      this.buttonDeleteText = this.$t('switchToFree')
      this.buttonCancelText = this.$t('nevermind')
    }
    for (let i = 0; i < this.text.terms.length; i++) {
      this.checkboxesArgs.push({
        term: this.text.terms[i],
        checked: false,
      })
    }
    if (this.team && !this.cancelSubscriptionProp) {
      let navigationlangugage = navigator.language.toUpperCase()
      const language = this.$i18n.locale
      const country = navigationlangugage.substring(navigationlangugage.length - 2, navigationlangugage.length)
      let res = await this.getPaymentDetails(this.team.teamId, navigationlangugage, country, language)
      if (res.upcomingInvoiceDateFormatted) {
        this.checkboxesArgs.push({
          term: this.$t('yourSubscriptionWillBeCanceledAtX', { x: res.upcomingInvoiceDateFormatted }),
          checked: false,
        })
      }
    }
  },
  methods: {
    async deleteAccount() {
      this.initial = false
      let args = true
      for (let i = 0; i < this.checkboxesArgs.length; i++) {
        if (!this.checkboxesArgs[i].checked) args = false
      }

      if (args) {
        if (this.confirmationStage) {
          if (this.team && this.team.name && this.teamName !== this.team.name) {
            args = false
            this.errorShake = true
            setTimeout(
              function () {
                this.errorShake = false
              }.bind(this),
              500,
            )
          } else {
            this.$emit('account-deleted', { password: this.password, teamName: this.teamName })
            this.$emit('close')
          }
        } else {
          this.confirmationStage = true
        }
      }
    },
    teamNameValidation() {
      return this.teamName === this.team.name
    },
  },
}
</script>

<style lang="scss" scoped>
section {
  display: flex;
  flex-direction: column;

  .input-std {
    :deep(.input-wrapper) {
      input {
        font-size: 14px;
        padding: 10px;
        width: 100%;
        color: $black;
        border: 1px solid $flax-bloom;
        background-color: #ffffff;
        transition: border-color 0.2s;
        line-height: normal;
      }
    }
  }

  .confirmation-reminder {
    background-color: $powder-pink;
    color: $red;
    font-family: $regular;
    font-size: 14px;
    line-height: 20px;

    margin: 0 -30px 24px -30px;
    padding: 20px 30px 20px 30px;
    @include respond-below(sm) {
      margin: 0 0 24px 0;
    }

    @include fade-in-opacity;
  }

  .confirmation {
    @include font-rule('averta-regular');
    font-size: 16px;
    margin-top: 0;
    margin-bottom: 20px;
  }
  .headsup {
    @include font-rule('averta-regular');
    font-size: 16px;
    margin-top: 0;
    margin-bottom: 20px;
    padding-left: 0px;
    padding-right: 0px;
  }
  .warning {
    @include font-rule('averta-bold');
    font-size: 16px;
    margin-bottom: 18px;
  }
  .check {
    margin-bottom: 18px;
  }
  .name-to-type {
    @include font-rule('averta-bold');
  }

  .buttons {
    display: flex;
    flex-direction: row;
    @include respond-below(xs) {
      flex-direction: column;
    }
    margin-top: 60px - 18px; // accounting for margin of the last checkbox
    button {
      flex: 1;
      padding: unset;
      text-align: center;
    }
    #delete-button {
      margin-right: 10px;
      @include respond-below(xs) {
        margin-right: 0;
        margin-bottom: 10px;
      }
    }
  }
  .animation-shake {
    animation: shake 0.5s linear;
    animation-iteration-count: infinite;
  }
}
</style>
